import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { LoadingService } from '../services/loading.service';

/**
 * The refresh every 401'd request is waiting on, shared so that concurrent failures trigger one refresh between
 * them. It must be able to fail: a waiter that only listens for a token hangs forever when none comes, and at startup
 * that hang is an APP_INITIALIZER that never settles — the app never boots and the page stays white.
 */
let refreshInFlight$: Observable<string> | null = null;

const addTokenAndLangToRequest = (request: any, token: string) => {
  // Get the language from localStorage
  const currentLang = localStorage.getItem('lang') || 'fr';

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Accept-Language': currentLang,
  };
  // A multipart body carries its boundary in the Content-Type, and only the browser knows it.
  // Forcing JSON here made every file upload unreadable to the server.
  if (!(request.body instanceof FormData)) headers['Content-Type'] = 'application/json';

  return request.clone({ setHeaders: headers });
};

const startRefresh = (authService: AuthService, loadingService: LoadingService): Observable<string> =>
  from(authService.refreshToken()).pipe(
    map((response: any) => {
      const token = response?.accessToken;
      if (!token) {
        throw new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized',
          error: 'Votre token est expiré, veuillez vous reconnecter',
        });
      }
      return token as string;
    }),
    // Runs once for the shared refresh, not once per waiter.
    catchError((error) => {
      loadingService.setLoading(false);
      authService.logout();
      return throwError(() => error);
    }),
    finalize(() => (refreshInFlight$ = null)),
    shareReplay(1),
  );

const handle401Error = (
  request: any,
  next: any,
  authService: AuthService,
  loadingService: LoadingService,
): Observable<any> => {
  refreshInFlight$ ??= startRefresh(authService, loadingService);
  return refreshInFlight$.pipe(switchMap((token) => next(addTokenAndLangToRequest(request, token))));
};

export const httpTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const loadingService = inject(LoadingService);

  if (authService.isAuthenticated() && !req.url.includes('auth/token')) {
    const token = authService.getToken();
    const requestWithToken = addTokenAndLangToRequest(req, token!);

    return next(requestWithToken).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return handle401Error(requestWithToken, next, authService, loadingService);
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  return next(req);
};
